
import { useState, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

import zohoLogo from '../assets/logos/zoho.png';
import salesforceLogo from '../assets/logos/salesforce.webp';
import microsoftLogo from '../assets/logos/microsoft.png';
import googleLogo from '../assets/logos/google.png';
import shopifyLogo from '../assets/logos/shopify.png';
import awsLogo from '../assets/logos/aws.webp';
import razorpayLogo from '../assets/logos/razorpay.webp';
import stripeLogo from '../assets/logos/Stripe_Logo.svg.webp';
import wordpressLogo from '../assets/logos/wordpress.webp';
import whatsappLogo from '../assets/logos/WhatsApp.svg.webp';
import githubLogo from '../assets/logos/github_PNG65.png';
import claudeLogo from '../assets/logos/claude.png';
import equencelogo from '../assets/logos/equence.png';
import openaiLogo from '../assets/logos/OpenAI-Logo.png';
import slackLogo from '../assets/logos/Slack-logo.jpg';
import twilioLogo from '../assets/logos/Twilio-Logo.wine.svg';


// Categorized integrations
const integrationCategories = [
  {
    title: 'Business & CRM',
    description:
      'Connect customer relationships, business applications and workflows.',
    items: [
      { name: 'Zoho', logo: zohoLogo, about: "Cloud suite for CRM, finance and business operations" },
      { name: 'Salesforce', logo: salesforceLogo, about: "The world's leading CRM platform" },
      { name: 'Microsoft', logo: microsoftLogo, about: "Microsoft 365, Dynamics 365 and Azure services" },
      { name: 'Google', logo: googleLogo, about: "Google Workspace, Maps and Cloud APIs" },
    ],
  },
  {
    title: 'Commerce & Payments',
    description:
      'Build seamless shopping experiences and secure payment solutions.',
    items: [
      { name: 'Shopify', logo: shopifyLogo, about: "E-commerce platform for online stores" },
      { name: 'Razorpay', logo: razorpayLogo, about: "Payments gateway for businesses in India" },
      { name: 'Stripe', logo: stripeLogo, about: "Online payments infrastructure for the internet" },
      { name: 'WordPress', logo: wordpressLogo, about: "Website and content management platform" },
    ],
  },
  {
    title: 'Cloud & AI Development',
    description:
      'Scalable infrastructure, modern development and AI-powered solutions.',
    items: [
      { name: 'AWS', logo: awsLogo, about: "Amazon's cloud computing platform" },
      { name: 'GitHub', logo: githubLogo, about: "Code hosting, version control and CI/CD" },
      { name: 'OpenAI', logo: openaiLogo, about: "GPT models for AI-powered features" },
      { name: 'Claude', logo: claudeLogo, about: "Anthropic's AI models for assistants and automation" },
    ],
  },
  {
    title: 'Communication & Automation',
    description:
      'Engage customers and automate communication across channels.',
    items: [
      { name: 'WhatsApp', logo: whatsappLogo, about: "WhatsApp Business messaging API" },
      { name: 'Equence', logo: equencelogo, about: "Messaging & automation integration" },
      { name: 'Slack', logo: slackLogo, about: "Team messaging with workflow alerts and bots" },
      { name: 'Twilio', logo: twilioLogo, about: "SMS, voice and messaging APIs" },
    ],
  },
];


// Logo tile; the company details fade in over it on hover (mouse), tap
// (touch/tablets) or Enter/Space (keyboard).
function IntegrationCard({ item, colors }) {
  const [hovered, setHovered] = useState(false);
  const [tapped, setTapped] = useState(false);
  const pointerType = useRef('mouse');
  const active = hovered || tapped;

  return (
    <div
      className="integration-card"
      tabIndex={0}
      aria-label={`${item.name}: ${item.about}`}
      style={{
        position: 'relative',
        height: '110px',
        boxSizing: 'border-box',
        background: '#ffffff',
        border: `1px solid ${active ? colors.accent : colors.border}`,
        borderRadius: '10px',
        padding: '1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        cursor: 'default',
        outlineOffset: '2px',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
        transform: active ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: active ? `0 10px 30px ${colors.border}` : 'none',
      }}
      onPointerDown={(e) => { pointerType.current = e.pointerType; }}
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') setHovered(true); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') setHovered(false); }}
      onClick={() => { if (pointerType.current !== 'mouse') setTapped(t => !t); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTapped(t => !t); }
      }}
      onBlur={() => setTapped(false)}
    >
      <img
        src={item.logo}
        alt=""
        loading="lazy"
        style={{
          display: 'block',
          width: 'auto',
          height: 'auto',
          maxWidth: 'min(130px, 85%)',
          maxHeight: '52px',
          objectFit: 'contain',
        }}
      />

      {/* Company details */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.3rem',
          padding: '0.7rem',
          textAlign: 'center',
          background: colors.surface,
          opacity: active ? 1 : 0,
          transition: 'opacity 0.25s ease',
          pointerEvents: 'none',
        }}
      >
        <span style={{ fontSize: '0.9rem', fontWeight: 700, color: colors.text }}>{item.name}</span>
        <span style={{ fontSize: '0.72rem', lineHeight: 1.4, color: colors.muted }}>{item.about}</span>
      </div>
    </div>
  );
}


function IntegrationCategory({ category, colors }) {
  return (
    <div className="integration-category">
      {/* Category heading */}
      <div
        className="integration-category-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1.25rem',
        }}
      >
        <h3
          style={{
            color: colors.text,
            fontSize: '1.15rem',
            fontWeight: 600,
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {category.title}
        </h3>

        <div
          style={{
            height: '1px',
            flex: 1,
            background: colors.border,
          }}
        />
      </div>

      <p
        style={{
          color: colors.muted,
          fontSize: '0.85rem',
          lineHeight: 1.6,
          margin: '0 0 1.25rem',
          maxWidth: '520px',
        }}
      >
        {category.description}
      </p>

      {/* Category logo grid */}
      <div
        className="integration-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
          gap: '0.8rem',
        }}
      >
        {category.items.map((item) => (
          <IntegrationCard
            key={item.name}
            item={item}
            colors={colors}
          />
        ))}
      </div>
    </div>
  );
}


export default function Integrations() {
  const { colors } = useTheme();

  const s = {
    section: {
      padding: '70px 5vw',
      background: colors.bg2,
    },

    eyebrow: {
      color: colors.muted,
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      textAlign: 'center',
      margin: '0 0 0.8rem',
    },

    title: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
      fontSize: 'clamp(1.8rem, 3.6vw, 3.2rem)',
      lineHeight: 1.05,
      letterSpacing: '1.5px',
      color: colors.heading,
      textAlign: 'center',
      margin: '0 0 1rem',
    },

    desc: {
      color: colors.muted,
      fontSize: '0.95rem',
      lineHeight: 1.8,
      maxWidth: '760px',
      fontWeight: 300,
      textAlign: 'center',
      margin: '0 auto 3rem',
    },

    categories: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: '3.5rem 3rem',
    },
  };

  return (
    <section id="integrations" style={s.section} className="integrations-header">
      {/* Section header */}
      <p style={s.eyebrow}>Platforms & Technologies</p>
      <h2 style={s.title}>INTEGRATIONS WE MASTER</h2>
      <p style={s.desc}>
        We connect your business with the world's leading platforms to
        create a unified, efficient technology ecosystem that eliminates
        silos.
      </p>

      {/* Categorized integrations */}
      <div style={s.categories} className="integration-categories">
        {integrationCategories.map((category) => (
          <IntegrationCategory
            key={category.title}
            category={category}
            colors={colors}
          />
        ))}
      </div>

      <style>{`
        .integration-card {
          min-width: 0;
        }

        @media (max-width: 1180px) {
          .integration-categories {
            grid-template-columns: 1fr !important;
            gap: 2.5rem !important;
          }
        }

        @media (max-width: 600px) {
          .integrations-header h2 {
            white-space: normal !important;
          }

          .integration-category-header h3 {
            font-size: 1rem !important;
          }

          .integration-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }

          .integration-card {
            padding: 0.8rem !important;
          }
        }
      `}</style>
    </section>
  );
}